/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX, ReactNode } from "react";

import { Skeleton } from "../Skeleton";
import type { ListSkeletonProps } from "./ListSkeletonProps";

import "./List.scss";

/**
 * Placeholder rows for {@link List}.
 *
 * Rows only — the returned fragment is meant to be mounted inside a
 * `.list__container`, which is what {@link List} does with it. That is
 * deliberate rather than incidental: the placeholders carry the same
 * `list__item` class as real rows, so a screen's row rhythm (My Hub spaces rows
 * with `.list__item + .list__item`) applies to the loading state for free and
 * cannot drift from the loaded one.
 *
 * Exported for the rare screen that owns its own container markup; most callers
 * should reach for `List`'s `isLoading` / `isLoadingMore` props instead.
 */
export const ListSkeleton: FunctionComponent<ListSkeletonProps> = (
  props: ListSkeletonProps
): JSX.Element => {
  const { count, renderItem, startIndex = 0, className } = props;

  const rowClassName: string = `list__item list__item--skeleton ${
    className || ""
  }`.trim();

  return (
    <>
      {Array.from(
        { length: Math.max(count, 0) },
        (_: unknown, offset: number): JSX.Element => {
          const index: number = startIndex + offset;
          return (
            <div
              key={`list-skeleton-${index}`}
              className={rowClassName}
              aria-hidden="true"
            >
              {renderItem === undefined ? (
                <Skeleton sequence={index} className="list__skeleton-bar" />
              ) : (
                (renderItem(index) as ReactNode)
              )}
            </div>
          );
        }
      )}
    </>
  );
};
